/**
 * Canonical UI copy — single source of truth for user-facing strings.
 * Same labels shown in Outlook and Gmail so users get a consistent experience.
 */
export const APP_NAME = 'AIRA';
export const APP_TAGLINE = 'AI Rental Assistant';
/**
 * Customer-facing pilot status. Surfaces that can draw a chip show this
 * beside the AIRA wordmark. Gmail Card Service cannot, so the Gmail card
 * builders add it in one place each: the logo-header subtitle
 * (`composeLogoHeader`), the draft-ready trace line, and the native compose
 * welcome card in Compose.gs. Remove at GA; do not rename the product.
 */
export const BETA_LABEL = 'Beta';
/** Gmail add-on listing name (Workspace add-on popup title). */
export const GMAIL_ADDON_DISPLAY_NAME = 'Sharefox - AIRA';
/** Card header line beside the logo (Gmail compose + processing chrome). */
export const GMAIL_CARD_BRAND_LINE = `${APP_NAME} - ${APP_TAGLINE}`;
/** Same as GMAIL_CARD_BRAND_LINE for Card Service header titles (HTML bold). */
export const GMAIL_CARD_BRAND_TITLE = `<b>${GMAIL_CARD_BRAND_LINE}</b>`;
/** Separator between intent label and confidence percentage. */
export const INTENT_SEPARATOR = '·';
/** Format intent + confidence consistently across platforms. */
export function formatIntentHeader(intentLabel, confidence) {
    const pct = Math.round(confidence * 100);
    return `${intentLabel} ${INTENT_SEPARATOR} ${pct}% confidence`;
}
/** Primary action button labels — single source of truth. */
export const BUTTON = {
    /** Kick off the AI draft flow. Same label in Outlook sidebar + Gmail compose. */
    generateDraft: 'Generate Draft',
    /** Create a Gmail / Outlook reply draft pre-filled with AIRA's text. */
    createReplyDraft: 'Create Reply Draft',
    /** Inject AIRA's text into the user's current compose window (Gmail compose). */
    insertIntoDraft: 'Insert into Draft',
    /** Compose UI-refresh footer label (mockup wording). Same action as insertIntoDraft. */
    insertAsDraft: 'Insert as draft',
    /** Default, signature-safe insert: appends below whatever is already composed. */
    appendToReply: 'Append to reply',
    /** Primary compose insert. Maps to IN_PLACE_INSERT (cursor): in a fresh reply
     *  the caret sits at the top, ABOVE the quoted original — the spot users
     *  expect. INSERT_AT_END is never used (it buries text inside the quote). */
    insertIntoReply: 'Insert Draft',
    /** Outlook mobile primary action. Reply injection is unsupported on Outlook
     *  iOS/Android (see officeMailbox.isOutlookMobile), so the button copies the
     *  draft to the clipboard and closes the task pane; the user then opens Reply
     *  and pastes. */
    copyAndClose: 'Copy & Close',
    /** Insert at the very start of the reply body. */
    insertAtTop: 'Insert at top',
    /** Insert at the cursor, replacing the current selection (closest to "replace"). */
    insertAtCursor: 'Insert at cursor',
    /** Kick the draft run from the composer screen (compose UI refresh). */
    generate: 'Generate',
    /**
     * @deprecated Prefer COMPOSE.processingCheckFirst / processingCheckAgain on the
     * Gmail compose processing card (honest tap-to-check loop). Kept for history.
     */
    getDraftStatus: 'Get draft status',
    /** Refine using the typed instructions note (compose UI refresh). */
    refine: 'Refine',
    insertClarification: 'Insert clarification',
    insertAcknowledgement: 'Insert acknowledgement',
    insertReply: 'Insert reply',
    /** Sidebar: generate AIRA's reply into an owned Gmail draft reply in the thread. */
    draftReply: 'Draft a reply',
    /** Sidebar: regenerate and replace the owned draft (preserves nothing typed there). */
    refineDraft: 'Refine draft',
    /** Sidebar: generate and append below the existing draft (keeps edits/signature). */
    appendDraft: 'Append to draft',
    /** Sidebar: regenerate and overwrite the whole owned draft. */
    replaceAllDraft: 'Replace all',
    /** Sidebar: regenerate and prepend above the existing draft body. */
    insertTopDraft: 'Insert at top',
    /** Sidebar history: write the selected past draft into the owned Gmail reply. */
    useThisDraft: 'Use this draft',
    tryAgainWithEntity: 'Try again →',
    forwardToManager: 'Escalate',
    seeBestGuess: 'See best-guess draft',
    regenerate: 'Regenerate',
    regenerateWithNote: 'Regenerate with my note',
    refresh: 'Refresh',
    startOver: 'Start Over',
    /** Step back to the input/Set up screen (keeps typed instructions). */
    back: 'Back',
    skip: 'Skip',
    like: 'Good draft',
    dislike: 'Needs improvement',
    /** Submit an optional free-text note after a thumbs up/down click. */
    send: 'Send',
};
/** Phase-appropriate subtitle shown in the header. */
export const HEADER_SUBTITLE = {
    idle: APP_TAGLINE,
    analyzing: 'Analyzing email',
    ready: 'Reply draft ready',
    compose: 'Compose assistant',
    inbox: 'Inbox triage',
    brand: 'by Sharefox',
};
/** Explains the data source before a user starts draft generation. */
export const DRAFT_DATA_NOTICE = "A draft email will be prepared using your store's live data.";
/** Status / progress copy. */
export const STATUS = {
    classifying: 'Classifying email intent...',
    checkingAvailability: 'Checking Sharefox availability...',
    generating: 'AIRA is thinking...',
    draftCreated: 'Draft reply created — open the reply window to review and send.',
    /** Outlook mobile: always-visible instruction under the Copy & Close button
     *  explaining what the button does and how to paste the copied draft. */
    mobileCopyCloseHelp: 'Pressing this button will copy the email draft, and close this window. Then you should click «Reply», and long-press → Paste.',
    /** Outlook mobile: shown only when the clipboard copy failed, so the user can
     *  select and copy the draft text manually (the pane stays open in that case). */
    draftCopyManual: 'Copy did not work automatically — select the draft above and copy it manually.',
    feedbackPositive: 'Thanks for the positive feedback!',
    feedbackNegative: 'Noted — use Refine to try again.',
    /** After the merchant sends an optional feedback note from an add-in. */
    feedbackNoteSaved: 'Thanks — shared with AIRA Product Department.',
    missingEntity: "AIRA couldn't complete this — a required detail is missing.",
    mediumConfidence: 'Medium confidence — review carefully before sending.',
    lowConfidence: "AIRA isn't confident enough to recommend a draft.",
    outOfScope: "This email is outside AIRA's scope.",
    toolFailure: (toolName) => `AIRA couldn't retrieve data from ${toolName}. A best-effort reply has been generated.`,
    /** Shown in the ThinkingPanel / SSE stream while a Sharefox tool is in flight. */
    toolCallStart: (toolName) => `Checking ${toolName}...`,
    /** Shown after a successful tool call in the ThinkingPanel. */
    toolCallSuccess: (toolName) => `${toolName} ✓`,
    /**
     * Shown when the BFF cannot reach the backend to draft a reply. Kept neutral so
     * the user does not get a fake-looking generic email — encourages them to retry.
     */
    fallbackDraft: "AIRA couldn't reach the drafting service. Please try Regenerate, or compose your reply manually.",
    /**
     * Shown when the backend reports the tenant/site is not draft-ready
     * (`activation_required`). This is an account/readiness state — NOT a
     * connectivity failure — so it must never be mapped to fallbackDraft.
     */
    activationRequired: 'AIRA must be activated from Sharefox Admin before drafting. Once your site is activated, try again.',
    /** Shown when the BFF cannot reach the backend on retry. */
    fallbackRetry: 'Sorry, unable to retry right now.',
};
/**
 * Compose UI-refresh copy (the 3-screen composer flow: input → preparing →
 * draft). Single source of truth so Outlook can adopt the same wording later.
 */
export const COMPOSE = {
    /** Screen 1 — intro paragraph shown under the AIRA header logo. */
    intro: `${DRAFT_DATA_NOTICE}<br><br>Add instructions or info`,
    /** Label above instructions inputs (welcome + draft-ready refine). */
    instructionsLabel: 'Add instructions or info',
    /** Screen 1 — examples shown as the instructions field's inline label (setTitle). */
    instructionsExamplesHint: "Eg. 'Thank them', 'Premium member', 'Be brief'",
    /** Regen / sidebar refine inputs (not the compose welcome / draft-ready refine). */
    instructionsHint: 'Add your instructions...',
    /** Screen 2 — card header subtitle (under the brand line beside the logo). */
    processingHeaderSubtitle: "Compose emails using your store's live data",
    /** Screen 2 — first section row under the header (hourglass icon in the card builder). */
    processingWorkingLine: 'Preparing your draft – usually takes 10–15 seconds',
    /** @deprecated Use processingHeaderSubtitle + processingWorkingLine. */
    preparing: "A draft email is being prepared using your store's live data...",
    /** Screen 2 — reassurance at bottom: the run is backend-durable + thread-keyed,
     *  so progress recovers on reopen (compose-recover / analyze are authoritative). */
    preparingHint: 'Working in the background - you can close and reopen the window.',
    /** Screen 2 — first paint CTA (user has not tapped poll yet). */
    processingCheckFirst: 'Still working – tap to check',
    /** Screen 2 — CTA after the user has tapped poll at least once. */
    processingCheckAgain: 'Still working, tap again',
    /** Screen 2 — hint under the CTA on first paint. */
    processingHintFirst: 'This panel only updates when you tap. You can close and reopen – work continues.',
    /** Screen 2 — hint when a poll found the run still on the same step. */
    processingHintStalled: 'Still on this step. Tap again in a few seconds, or close and reopen – work continues.',
    /** Screen 2 — hint when a poll advanced a progress step but the draft is not ready. */
    processingHintAdvanced: 'Progress updated. Tap again if the draft is not ready yet, or close and reopen – work continues.',
    /** Host toast after a still-running poll that did not advance a step. */
    processingToastStalled: (phase) => `Still working – ${phase}. Tap again in a few seconds.`,
    /** Screen 3 — generic result intro (kept content-neutral; honest). */
    resultIntro: "Here's a draft reply. Please review and adjust if needed before inserting.",
    /** Screen 3 — collapsible thinking-trace header prefix. */
    traceTitle: 'Draft is Ready',
    /** Screen 3 — collapsible thinking-trace expand/collapse labels. */
    traceSeeDetails: 'See details',
    traceHideDetails: 'Hide details',
    /** Screen 3 — collapsible sources expand/collapse labels. */
    sourcesExpand: 'See Sources',
    sourcesCollapse: 'Hide sources',
    /** Screen 3 — feedback prompt. */
    feedbackPrompt: 'Is this helpful?',
    /** Placeholder under thumbs-up after the merchant taps 👍. */
    feedbackNotePlaceholderLike: 'Describe why it was good',
    /** Placeholder under thumbs-down after the merchant taps 👎. */
    feedbackNotePlaceholderDislike: 'Describe the problem',
    /** Screen 3 — hint explaining the insert-position choices. */
    insertHint: 'Insert into reply drops the text at the top, above the quoted thread. Insert at cursor places it where your cursor is (replacing any selected text).',
    /** Shown on every screen, greyed, beneath the action area. */
    disclaimer: 'AIRA can make mistakes. Please review.',
    /** Compose/sidebar entry gate — bold title on the "not draft-ready yet" card
     *  (backend activation_required). Body copy is STATUS.activationRequired. */
    notReadyTitle: 'Not activated yet',
};
/**
 * Sources-section copy shared by both platforms. Knowledge rows keep the entry
 * title as their header; Live API rows all use `liveHeader` as the header with
 * a per-tool description below. `{name}`/`{id}` placeholders are filled by the
 * backend (which has the tool args/results); templates without placeholders are
 * used verbatim. Only the tools listed here ever surface as sources — a strict
 * whitelist, so write tools (putOrderNote, cancelOrder) and unlisted read
 * tools stay invisible by construction.
 */
export const SOURCES = {
    /** Header shown on every Live API source row. */
    liveHeader: 'Live data',
    /** Label on the per-knowledge-source affordance deep-linking to the Admin editor. */
    updateKnowledge: 'Update knowledge',
    /** Per-tool description templates for Live API source rows. */
    live: {
        getProductCategories: 'Your Sharefox product categories.',
        getProducts: 'Your Sharefox product catalog',
        getProductWithName: 'Product information: {name} (ID {id})',
        getProductNoName: 'Product information (ID {id})',
        getOrder: 'Order information in Sharefox Admin',
        getProductAvailabilitySingleWithName: 'Live availability for: {name} (ID {id})',
        getProductAvailabilitySingleNoName: 'Live availability (ID {id})',
        getProductAvailabilityMultiple: 'Live availability data for multiple products',
        getCustomer: 'Customer information in Sharefox Admin',
    },
};
/**
 * Sidebar (contextual trigger) copy. The sidebar can generate a draft for an
 * open email, but — unlike the compose add-on — it cannot inject into an open
 * inline reply; it creates a Gmail draft reply the user opens. Hence two
 * distinct "done" lines depending on where the draft was generated.
 */
export const SIDEBAR = {
    /** Header subtitle on the reduced sidebar card. */
    composerOnly: 'Inline composer only',
    /** Running header. */
    preparing: 'Drafting a reply …',
    /** Done status — draft generated FROM the sidebar (a Gmail draft reply created).
     *  Always point at Drafts: Gmail's Reply button opens a NEW empty reply, not
     *  AIRA's saved draft, so "open the reply" sends users into an empty box. */
    doneSidebar: 'Draft ready — open it from your Drafts folder to review and send.',
    /** Done status — a finished run exists for this thread. Deliberately neutral:
     *  the recap can render long after compose closed (history/thread cache), so
     *  "added to your reply" would claim something we can no longer see. */
    doneCompose: 'Draft completed for this thread.',
    /** Empty-card invitation to let the sidebar draft a reply itself. (Must read
     *  standalone — it is the FIRST line of the empty card, so no leading "Or".) */
    draftInvite: 'Let AIRA draft a reply to this email. The reply is saved to your Drafts folder, ready to review, edit and send.',
    /** Recap controls hint. The sidebar regenerates from the customer's email (not
     *  from anything typed in an open compose box) and saves the result as AIRA's
     *  Gmail draft on the thread — it cannot write into an open reply box. */
    refineHint: "Add a note to steer the rewrite — AIRA redrafts from the customer's email. Refine draft replaces AIRA's saved draft for this thread; Append to draft adds the new version below it. The result lands in your Drafts folder.",
    /** History item — invitation to push a past draft into the owned reply. */
    useThisHint: "Save this version as the thread's Gmail draft — you'll find it in your Drafts folder.",
};
/** Screen 2 preparing checklist (4 rows; superseded by grouped progress when available). */
export const COMPOSE_PROGRESS_STEPS = [
    { stage: 'classify', label: 'Identified customer interest', seconds: 0.8 },
    { stage: 'retrieve', label: 'Searched for relevant store knowledge', seconds: 1.2 },
    { stage: 'retrieve', label: 'Looked into the specific order', seconds: 0.5 },
    { stage: 'draft', label: 'Drafting reply', seconds: 0.9 },
];
/** Screen 3 collapsible thinking-trace rows (3 rows, all done on a finished run). */
export const COMPOSE_TRACE_STEPS = [
    { stage: 'classify', label: 'AIRA read email', seconds: 0.8 },
    { stage: 'retrieve', label: 'AIRA searched for knowledge', seconds: 0.9 },
    { stage: 'draft', label: 'AIRA drafted reply', seconds: 0.9 },
];
/** Placeholder total "thinking" time for the Screen 3 trace header. TODO(real-timings). */
export const PLACEHOLDER_THOUGHT_SECONDS = 12;
/** Error messages — shown when the flow breaks. */
export const ERRORS = {
    loadEmail: "AIRA couldn't load this email. Try reloading.",
    streamInterrupted: "AIRA's response was interrupted. Please try again.",
    backendUnavailable: 'AIRA is unavailable right now. Please try again later.',
    noDraftAvailable: 'No draft text available.',
    openEmailFirst: 'Open an email first, then AIRA will generate a reply draft.',
    regenerateLimitReached: 'Maximum refinements reached (3). Please start over.',
};
//# sourceMappingURL=copy.js.map